import { useState } from "react";
import { BrandLogo } from "@/components/ui/brand-logo";
import { cn } from "@/lib/utils";

/**
 * A barra que atravessa a calha. Cada etapa do arranque monta o seu próprio ecrã de espera, e uma animação que recomeça
 * do zero a cada montagem vê-se como um soluço: o atraso negativo, medido no relógio da página, retoma-a onde ia, e o
 * olho lê as várias etapas como uma só espera. `overflow-hidden` recorta o traço nas pontas.
 */
function LoadingBar() {
  // Lido uma só vez, ao montar (um valor impuro no corpo mudaria a cada render).
  const [phase] = useState(() => (typeof performance === "undefined" ? 0 : Math.round(performance.now() % 1400)));
  return (
    <div className="h-px w-40 overflow-hidden rounded-full bg-foreground/8" aria-hidden>
      <div className="h-full w-1/4 animate-indeterminate rounded-full bg-primary" style={{ animationDelay: `-${phase}ms` }} />
    </div>
  );
}

interface LoadingPageProps {
  /** Texto por baixo. Os guardas de arranque não o usam: um texto que muda a cada etapa lê-se como «outro ecrã». */
  message?: string;
  /** Ecrã inteiro (arranque da app) ou bloco embutido numa página. */
  fullScreen?: boolean;
  className?: string;
  size?: "sm" | "md" | "lg" | "xl";
}

/**
 * O ecrã de espera.
 *
 * Em ecrã inteiro — que é o arranque da aplicação — mostra a marca com uma
 * barra de progresso indeterminada por baixo. Um spinner sozinho no meio do
 * vazio lê-se como "isto encravou"; a marca e uma barra a andar leem-se como
 * "a aplicação está a abrir". É a mesma cena do ecrã de entrada, e a passagem
 * de um para o outro deixa de ter um salto.
 *
 * Embutido numa página, onde já há contexto à volta, basta o arco a rodar.
 */
export function LoadingPage({
  message,
  fullScreen = true,
  className,
}: LoadingPageProps) {
  if (!fullScreen) {
    return (
      <div className={cn("flex flex-col items-center justify-center gap-3 py-16", className)} role="status" aria-live="polite">
        <LoadingBar />
        {message && <p className="text-[13px] text-muted-foreground">{message}</p>}
      </div>
    );
  }

  return (
    <div
      className={cn(
        "flex h-dvh flex-col items-center justify-center gap-8 bg-background",
        className
      )}
      role="status"
      aria-live="polite"
    >
      <BrandLogo className="h-12 w-auto max-w-[260px] object-contain" fallbackClassName="text-2xl" />

      <div className="flex flex-col items-center gap-4">
        {/* A calha e o traço que a atravessa. `overflow-hidden` é o que recorta
            o traço nas pontas — sem isso ele passeia-se pelo ecrã fora. */}
        <LoadingBar />

        {message && (
          <p className="text-[13px] tracking-tight text-muted-foreground">{message}</p>
        )}
      </div>
    </div>
  );
}
